import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FeatureFlagStrategy } from '../types';
import { useAppFlagStore } from './use-feature-flag';

/**
 * `TRUELARK_AI_VOICE` for bridgeline AI Voice and `WEAVE_VOICE_AIR` for steelthread AI Voice
 */
export const VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS = [Feature.TRUELARK_AI_VOICE, Feature.WEAVE_VOICE_AIR] as const;

const DEFAULT_ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

const evaluateIsActive = (values: boolean[], strategy: FeatureFlagStrategy): boolean => {
  if (values.length === 0) return false;
  return strategy === 'ALL' ? values.every(Boolean) : values.some(Boolean);
};

/**
 * Resolves `TRUELARK_AI_VOICE` and `WEAVE_VOICE_AIR` for the provided locations.
 * Returns combined and per-flag activity states and location maps.
 *
 * `strategy` defaults to `'ANY'` and controls whether one or every location must
 * be active. `activeStates` defaults to `[State.ACTIVE, State.PROMOTE]`.
 */
export const useVoiceAIRCustomizationFlag = (
  locationIds: string[],
  activeStates: State[] = DEFAULT_ACTIVE_STATES,
  strategy: FeatureFlagStrategy = 'ANY'
) => {
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  return useMemo(() => {
    const truelarkAIRawMap = getCustomizationFlagLocationMap({
      locationIds,
      featureEnum: Feature.TRUELARK_AI_VOICE,
    });
    const weaveAIRawMap = getCustomizationFlagLocationMap({
      locationIds,
      featureEnum: Feature.WEAVE_VOICE_AIR,
    });

    const truelarkVoiceAIFlagValueMap: Record<string, boolean> = {};
    const weaveVoiceAIFlagValueMap: Record<string, boolean> = {};
    const locationFlagValueMap: Record<string, boolean> = {};

    for (const locationId of locationIds) {
      const truelarkVoiceAiCFData = truelarkAIRawMap[locationId];
      const weaveVoiceAiCFData = weaveAIRawMap[locationId];

      const isTruelarkActive = truelarkVoiceAiCFData
        ? activeStates.includes(truelarkVoiceAiCFData.state ?? State.HIDE)
        : false;
      const isWeaveActive = weaveVoiceAiCFData ? activeStates.includes(weaveVoiceAiCFData.state ?? State.HIDE) : false;

      if (truelarkVoiceAiCFData) {
        truelarkVoiceAIFlagValueMap[locationId] = isTruelarkActive;
      }
      if (weaveVoiceAiCFData) {
        weaveVoiceAIFlagValueMap[locationId] = isWeaveActive;
      }

      // If at least one flag returned data for this location, record combined state
      if (truelarkVoiceAiCFData || weaveVoiceAiCFData) {
        locationFlagValueMap[locationId] = isTruelarkActive || isWeaveActive;
      }
    }

    const truelarkVoiceAiCFValues = Object.values(truelarkVoiceAIFlagValueMap);
    const weaveVoiceAiCFValues = Object.values(weaveVoiceAIFlagValueMap);
    const combinedVoiceAiCFValues = Object.values(locationFlagValueMap);

    const truelarkVoiceAIActive = evaluateIsActive(truelarkVoiceAiCFValues, strategy);
    const weaveVoiceAIActive = evaluateIsActive(weaveVoiceAiCFValues, strategy);
    const isActive = evaluateIsActive(combinedVoiceAiCFValues, strategy);

    return {
      isActive,
      locationFlagValueMap,
      truelarkVoiceAIActive,
      weaveVoiceAIActive,
      truelarkVoiceAIFlagValueMap,
      weaveVoiceAIFlagValueMap,
    };
  }, [activeStates, getCustomizationFlagLocationMap, locationIds, strategy]);
};
